import React, { useState, useEffect, useRef } from 'react';
import { editWorkOrder, getCompanySettings, fetchQuickBooksCustomers, fetchQuickBooksItems, fetchQuickBooksCompanyInfo, fetchQuickBooksServiceMappings, fetchCustomer, fetchRetailCustomerSetting, WORKORDER_BASE_URL, createJobInvoice, JOBS_BASE_URL, fetchWorkOrderInvoice, updateWorkOrderInvoice, fetchJobInvoice, updateJobInvoice, fetchJobInvoicePdf, fetchWorkOrderInvoicePdf } from './api';
import SendInvoiceDialog from './SendInvoiceDialog';
import { printPdf } from '../utils/printPdf';
import './InvoiceModal.css';
import { getTodayEastern } from '../utils/timezone';

// Same rule as QuickBooksServiceMapping.normalize on the backend: 'Mount & Balance' -> 'mount&balance'
const normalizeServiceKey = (name) => (name || '').toLowerCase().replace(/\s+/g, '');

// Recalculate subtotal, tax and total after the line items change
const withTotals = (data, lineItems) => {
  const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const taxAmount = (subtotal * (parseFloat(data.tax_rate) || 0)) / 100;
  return { ...data, line_items: lineItems, subtotal, tax_amount: taxAmount, total: subtotal + taxAmount };
};

// Select a QuickBooks item on a line. Jobs always take the item's price (they have no
// price of their own); work orders keep their price unless the line has none yet.
const withSelectedItem = (lineItem, itemId, qbItem, isJobInvoice) => {
  const selectedUnitPrice = qbItem ? (parseFloat(qbItem.unit_price) || 0) : 0;
  const quantity = parseFloat(lineItem.quantity) || 1;
  let rate = lineItem.rate;
  let amount = lineItem.amount;

  if (itemId && selectedUnitPrice > 0) {
    const hasExistingPrice = (parseFloat(lineItem.rate) || 0) > 0 || (parseFloat(lineItem.amount) || 0) > 0;
    if (isJobInvoice || !hasExistingPrice) {
      rate = selectedUnitPrice;
      amount = quantity * selectedUnitPrice;
    }
  }

  return { ...lineItem, selected_item_id: itemId, rate, amount };
};

// customerId: the app customer being invoiced, used for its QuickBooks link and name
// editInvoice: edit the work order's or job's existing (unpaid) QuickBooks invoice instead of creating one
const InvoiceModal = ({ workOrder, onClose, onConfirm, isJobInvoice = false, jobId = null, customerId = null, editInvoice = false }) => {
  console.log('=== INVOICE MODAL RENDERED ===');
  console.log('Work order:', workOrder);
  console.log('Is Job Invoice:', isJobInvoice);
  console.log('Job ID:', jobId);
  
  const [invoiceData, setInvoiceData] = useState({
    customer_name: '',
    private_note: '',
    doc_number: '',
    po_number: '',
    selected_customer_id: '',
    line_items: [
      {
        id: 1,
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
        selected_item_id: '',
        service_date: getTodayEastern(),
        service_date_edited: false
      }
    ],
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    total: 0,
    invoice_date: getTodayEastern()
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // The create-invoice reply once QuickBooks has made the invoice (shows the Send / Print / Done screen)
  const [created, setCreated] = useState(null);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [markingAsPaid, setMarkingAsPaid] = useState(false);
  const [paidInvoiceNumber, setPaidInvoiceNumber] = useState('00000');
  
  // QuickBooks data state
  const [qbCustomers, setQbCustomers] = useState([]);
  const [qbItems, setQbItems] = useState([]);
  const [qbCompanyInfo, setQbCompanyInfo] = useState(null);
  const [serviceMappings, setServiceMappings] = useState(null);
  const [loadingQbData, setLoadingQbData] = useState(false);
  const [qbNotice, setQbNotice] = useState('');
  const [customerNotice, setCustomerNotice] = useState('');
  const [appCustomer, setAppCustomer] = useState(null);
  const [retailCustomerName, setRetailCustomerName] = useState('Retail');
  
  // Customer search state
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [existingInvoice, setExistingInvoice] = useState(null);

  // The job or work order being invoiced
  const entityId = isJobInvoice ? jobId : workOrder?.id;

  // Editing: start from the invoice's current lines in QuickBooks
  useEffect(() => {
    if (!editInvoice || !entityId) return;
    (isJobInvoice ? fetchJobInvoice(entityId) : fetchWorkOrderInvoice(entityId))
      .then(data => {
        setExistingInvoice(data);
        const lineItems = data.line_items.map((line, index) => ({
          ...line,
          id: index + 1,
          service_date: line.service_date || '',
          service_date_edited: true
        }));
        setInvoiceData(prev => withTotals({ ...prev, tax_rate: 0, invoice_date: data.invoice_date || prev.invoice_date }, lineItems));
      })
      .catch(err => setError(err.response?.data?.error || 'Could not load the invoice from QuickBooks.'));
  }, [editInvoice, entityId, isJobInvoice]);

  useEffect(() => {
    if (workOrder && !editInvoice) {
      // Initialize with work order data
      const workOrderPrice = workOrder.price || 0;
      const numberOfWheels = workOrder.number_of_wheels || 1;
      
      // For job invoices, pre-populate PO number from the workOrder.notes (which contains job.po)
      const poNumber = isJobInvoice ? (workOrder.notes || '') : '';
      
      // Keep a QuickBooks customer already chosen (e.g. the linked one) if this re-runs
      setInvoiceData(prev => {
        const invoiceDate = prev.invoice_date || getTodayEastern();
        return {
          // Billing address is only filled in when the Retail QuickBooks customer is selected
          customer_name: prev.customer_name || '',
          private_note: workOrder.notes || '',
          po_number: poNumber, // Pre-populate PO for job invoices
          selected_customer_id: prev.selected_customer_id || '',
          line_items: [
            {
              id: 1,
              description: workOrder.vehicle || '',
              quantity: numberOfWheels, // Use number of wheels from work order
              rate: workOrderPrice / numberOfWheels, // Rate = Total Price ÷ Number of Wheels
              amount: workOrderPrice, // Amount = Total Price from work order
              selected_item_id: '',
              service_date: invoiceDate,
              service_date_edited: false
            }
          ],
          subtotal: workOrderPrice,
          tax_rate: 0,
          tax_amount: 0,
          total: workOrderPrice,
          invoice_date: invoiceDate
        };
      });
    }
  }, [workOrder, isJobInvoice, editInvoice]);

  useEffect(() => {
    // Fetch paid invoice number setting and QuickBooks data
    const fetchInitialData = async () => {
      try {
        // Fetch company settings
        const settings = await getCompanySettings();
        if (settings.paid_invoice_number) {
          setPaidInvoiceNumber(settings.paid_invoice_number);
        }
        
        // Fetch QuickBooks data
        setLoadingQbData(true);
        const [customers, items, companyInfo, mappings, linkedCustomer, retailSetting] = await Promise.all([
          fetchQuickBooksCustomers().catch(err => {
            console.warn('Failed to fetch QuickBooks customers:', err);
            return [];
          }),
          fetchQuickBooksItems().catch(err => {
            console.warn('Failed to fetch QuickBooks items:', err);
            return [];
          }),
          fetchQuickBooksCompanyInfo().catch(err => {
            console.warn('Failed to fetch QuickBooks company info:', err);
            return null;
          }),
          fetchQuickBooksServiceMappings().catch(err => {
            console.warn('Failed to fetch QuickBooks service mappings:', err);
            return null;
          }),
          (customerId ? fetchCustomer(customerId) : Promise.resolve(null)).catch(err => {
            console.warn('Failed to fetch customer:', err);
            return null;
          }),
          fetchRetailCustomerSetting().catch(err => {
            console.warn('Failed to fetch retail customer setting:', err);
            return null;
          })
        ]);
        
        // Set before the QuickBooks customers: the linked-customer effect runs once those
        // arrive and needs these already in place
        if (retailSetting?.retail_customer_name) {
          setRetailCustomerName(retailSetting.retail_customer_name);
        }
        setAppCustomer(linkedCustomer);
        setQbCustomers(customers);
        setQbItems(items);
        setQbCompanyInfo(companyInfo);
        setServiceMappings(mappings);
        
        // Initialize filtered customers
        setFilteredCustomers(customers);
        
      } catch (error) {
        console.error('Error fetching initial data:', error);
        // Keep default values if fetch fails
      } finally {
        setLoadingQbData(false);
      }
    };
    fetchInitialData();
  }, [customerId]);

  // Once QuickBooks items and service mappings are loaded, pre-select the mapped item on
  // the first line and add a Tire Disposal line if the work order/job asks for one.
  // Safe to re-run: it never replaces an item already chosen or adds a second tire line.
  useEffect(() => {
    if (editInvoice || !workOrder || !serviceMappings || qbItems.length === 0) return;

    const mappedItemId = (key) => serviceMappings.services.find(s => s.key === key)?.qb_item_id || '';
    const serviceName = isJobInvoice ? workOrder.service : workOrder.service_requested;
    const serviceItemId = mappedItemId(normalizeServiceKey(serviceName));
    const serviceItem = qbItems.find(item => item.id === serviceItemId);

    let tireItem = null;
    if (workOrder.tire_disposal) {
      const tireItemId = mappedItemId(serviceMappings.tire_disposal_key);
      tireItem = qbItems.find(item => item.id === tireItemId) || null;
      setQbNotice(tireItem ? '' : 'Tire disposal is checked, but no QuickBooks item is mapped to Tire Disposal. Add it in Settings › QuickBooks › Service Mapping, or add the line by hand.');
    }

    setInvoiceData(prev => {
      let lineItems = prev.line_items;

      if (serviceItem && lineItems.length > 0 && !lineItems[0].selected_item_id) {
        lineItems = [withSelectedItem(lineItems[0], serviceItem.id, serviceItem, isJobInvoice), ...lineItems.slice(1)];
      }

      if (tireItem && !lineItems.some(item => item.selected_item_id === tireItem.id)) {
        const wheels = parseFloat(workOrder.number_of_wheels) || 1;
        const rate = parseFloat(tireItem.unit_price) || 0;
        lineItems = [...lineItems, {
          id: Math.max(...lineItems.map(item => item.id), 0) + 1,
          description: tireItem.description || tireItem.name,
          quantity: wheels,
          rate,
          amount: wheels * rate,
          selected_item_id: tireItem.id,
          service_date: prev.invoice_date,
          service_date_edited: false
        }];
      }

      return lineItems === prev.line_items ? prev : withTotals(prev, lineItems);
    });
  }, [workOrder, isJobInvoice, serviceMappings, qbItems, editInvoice]);

  // Filter customers based on search term
  useEffect(() => {
    if (customerSearchTerm.trim() === '') {
      setFilteredCustomers(qbCustomers);
    } else {
      const filtered = qbCustomers.filter(customer => 
        customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        (customer.company_name && customer.company_name.toLowerCase().includes(customerSearchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearchTerm, qbCustomers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Service dates follow the invoice date until someone changes them on the line
      if (name === 'invoice_date') {
        newData.line_items = prev.line_items.map(item =>
          item.service_date_edited ? item : { ...item, service_date: value }
        );
      }

      // Calculate totals when tax rate changes
      if (name === 'tax_rate') {
        const taxRate = parseFloat(value) || 0;
        const subtotal = parseFloat(prev.subtotal) || 0;
        const taxAmount = (subtotal * taxRate) / 100;
        newData.tax_amount = taxAmount;
        newData.total = subtotal + taxAmount;
      }
      
      return newData;
    });
  };

  const handleLineItemChange = (lineItemId, field, value) => {
    setInvoiceData(prev => {
      const newLineItems = prev.line_items.map(item => {
        if (item.id === lineItemId) {
          const updatedItem = { ...item, [field]: value };

          if (field === 'service_date') {
            updatedItem.service_date_edited = true;
          }
          
          // Calculate amount when quantity or rate changes
          if (field === 'quantity' || field === 'rate') {
            const quantity = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
            const rate = parseFloat(field === 'rate' ? value : item.rate) || 0;
            updatedItem.amount = quantity * rate;
            console.log(`Updated ${field}:`, value, 'New amount:', updatedItem.amount);
          }
          
          // If amount is changed directly, recalculate rate based on quantity
          if (field === 'amount') {
            const quantity = parseFloat(item.quantity) || 1;
            const amount = parseFloat(value) || 0;
            updatedItem.rate = quantity > 0 ? amount / quantity : amount;
          }
          
          return updatedItem;
        }
        return item;
      });
      
      // Recalculate totals
      const subtotal = newLineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      const taxAmount = (subtotal * (parseFloat(prev.tax_rate) || 0)) / 100;
      const total = subtotal + taxAmount;
      
      return {
        ...prev,
        line_items: newLineItems,
        subtotal,
        tax_amount: taxAmount,
        total
      };
    });
  };

  const addLineItem = () => {
    setInvoiceData(prev => {
      const newId = Math.max(...prev.line_items.map(item => item.id), 0) + 1;
      const newLineItem = {
        id: newId,
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
        selected_item_id: '',
        service_date: prev.invoice_date,
        service_date_edited: false
      };
      
      return {
        ...prev,
        line_items: [...prev.line_items, newLineItem]
      };
    });
  };

  const removeLineItem = (lineItemId) => {
    setInvoiceData(prev => {
      if (prev.line_items.length <= 1) return prev; // Don't remove the last item
      
      const newLineItems = prev.line_items.filter(item => item.id !== lineItemId);
      const subtotal = newLineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      const taxAmount = (subtotal * (parseFloat(prev.tax_rate) || 0)) / 100;
      const total = subtotal + taxAmount;
      
      return {
        ...prev,
        line_items: newLineItems,
        subtotal,
        tax_amount: taxAmount,
        total
      };
    });
  };

  const handleCustomerSearch = (e) => {
    const searchTerm = e.target.value;
    setCustomerSearchTerm(searchTerm);
    setShowCustomerDropdown(true);
  };

  const appCustomerName = appCustomer?.name || workOrder?.customer_name || '';
  const isRetailCustomer = (qbCustomer) =>
    !!qbCustomer && qbCustomer.name.trim().toLowerCase() === retailCustomerName.trim().toLowerCase();
  const selectedQbCustomer = qbCustomers.find(c => c.id === invoiceData.selected_customer_id);

  // Retail is shared by many app customers, so put this customer's name in the billing
  // address. Any other QuickBooks customer uses the address stored in QuickBooks.
  const handleCustomerSelect = (customer) => {
    setInvoiceData(prev => ({
      ...prev,
      selected_customer_id: customer.id,
      customer_name: isRetailCustomer(customer) ? appCustomerName : ''
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);
    setCustomerNotice('');
  };

  // Pre-select the QuickBooks customer linked in customer settings, once both are loaded.
  // Customers without a link start with nothing selected.
  const linkApplied = useRef(false);
  useEffect(() => {
    if (linkApplied.current || !appCustomer || qbCustomers.length === 0) return;
    linkApplied.current = true;
    if (!appCustomer.qb_customer_id) return;

    const linked = qbCustomers.find(c => c.id === appCustomer.qb_customer_id);
    if (linked) {
      handleCustomerSelect(linked);
    } else {
      setCustomerNotice(`This customer is linked to "${appCustomer.qb_customer_name || appCustomer.qb_customer_id}", which wasn't found in QuickBooks (it may be inactive). Choose a customer below.`);
    }
  // handleCustomerSelect only reads appCustomer and retailCustomerName, which are loaded with qbCustomers
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appCustomer, qbCustomers]);

  const handleCustomerSearchFocus = () => {
    setShowCustomerDropdown(true);
  };

  const handleCustomerSearchBlur = () => {
    // Delay hiding dropdown to allow for click events
    setTimeout(() => {
      setShowCustomerDropdown(false);
    }, 200);
  };

  const handleItemChange = (lineItemId, e) => {
    const itemId = e.target.value;
    const selectedItem = qbItems.find(item => item.id === itemId);

    setInvoiceData(prev => withTotals(prev, prev.line_items.map(item =>
      item.id === lineItemId ? withSelectedItem(item, itemId, selectedItem, isJobInvoice) : item
    )));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    console.log('=== INVOICE MODAL SUBMIT STARTED ===');
    console.log('Event:', e);
    console.log('Work order:', workOrder);
    console.log('Invoice data:', invoiceData);
    console.log('Is Job Invoice:', isJobInvoice);
    console.log('Job ID:', jobId);
    console.log('Form element:', e.target);
    
    if (editInvoice) {
      await handleSaveInvoiceEdits();
      return;
    }

    // No silent fallback to Retail: someone has to choose the QuickBooks customer
    if (qbCustomers.length > 0 && !invoiceData.selected_customer_id) {
      setError('Choose a QuickBooks customer before creating the invoice.');
      return;
    }

    setLoading(true);
    setError('');

    console.log(`Creating invoice for ${isJobInvoice ? 'job' : 'work order'}:`, workOrder.id);
    console.log('Invoice data:', invoiceData);
    console.log('Line items:', invoiceData.line_items);
    console.log('Auth token:', localStorage.getItem('authToken'));

    try {
      // Use different endpoints for jobs vs work orders
      const baseUrl = isJobInvoice ? JOBS_BASE_URL : WORKORDER_BASE_URL;
      const entityId = isJobInvoice ? jobId : workOrder.id;
      
      const response = await fetch(`${baseUrl}create-invoice/${entityId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          invoice_data: invoiceData
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        // Stay open so the new invoice can be sent or printed; Done hands it back (onConfirm)
        setCreated(data);
      } else {
        console.error('Invoice creation failed:', data);
        setError(data.error || `Failed to create invoice. Status: ${response.status}`);
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSaveInvoiceEdits = async () => {
    if (!existingInvoice) return;
    setLoading(true);
    setError('');
    try {
      const data = await (isJobInvoice
        ? updateJobInvoice(entityId, invoiceData.line_items)
        : updateWorkOrderInvoice(entityId, invoiceData.line_items));
      // Stay open so the corrected invoice can be sent or printed; Done hands it back (onConfirm)
      setCreated({ ...data, invoice_number: existingInvoice.invoice_number, edited: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update the invoice in QuickBooks.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    console.log('=== MARK AS PAID CLICKED ===');
    console.log('Work order:', workOrder);
    
    setMarkingAsPaid(true);
    setError('');

    try {
      // Mark as paid (without QuickBooks) with the configured paid invoice number; status is left alone
      // Include all current work order data to satisfy PUT requirements
      const updatedWorkOrder = await editWorkOrder(workOrder.id, {
        ...workOrder,
        mark_as_paid: true,
        invoice_number: paidInvoiceNumber
      });

      console.log('Work order marked as paid:', updatedWorkOrder);
      
      // Call onConfirm with the updated work order data
      const confirmData = {
        ...updatedWorkOrder,
        invoice_number: paidInvoiceNumber
      };
      console.log('Calling onConfirm with:', confirmData);
      onConfirm(confirmData);
      
      // Close modal after a brief success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error marking work order as paid:', err);
      setError('Failed to mark as paid. Please try again.');
    } finally {
      setMarkingAsPaid(false);
    }
  };

  if (!workOrder) return null;

  if (created) {
    const record = (isJobInvoice ? created.updated_job : created.updated_work_order) || {};
    // Back to the job or work order, which closes as before with the invoice recorded
    const handleDone = () => onConfirm(created);
    const handlePrintCreated = async () => {
      setPrinting(true);
      setError('');
      try {
        await printPdf(() => (isJobInvoice ? fetchJobInvoicePdf(entityId) : fetchWorkOrderInvoicePdf(entityId)));
      } catch (err) {
        setError(err.response?.data?.error || 'Could not get the invoice from QuickBooks.');
      } finally {
        setPrinting(false);
      }
    };
    const handleSent = (data) => {
      const sentFields = { invoice_sent_at: data.invoice_sent_at, invoice_sent_to: data.invoice_sent_to };
      setCreated((prev) => ({
        ...prev,
        sent_here: true,
        ...(prev.updated_job && { updated_job: { ...prev.updated_job, ...sentFields } }),
        ...(prev.updated_work_order && { updated_work_order: { ...prev.updated_work_order, ...sentFields } }),
      }));
    };

    return (
      <div className="invoice-modal-overlay">
        <div className="invoice-modal invoice-created-modal">
          <div className="invoice-modal-header">
            <div className="invoice-header-left">
              <h2>{created.edited ? 'Invoice Updated' : 'Invoice Created'}</h2>
              {qbCompanyInfo && (
                <div className="qb-company-info">
                  <small>{qbCompanyInfo.company_name}</small>
                </div>
              )}
            </div>
            <div className="invoice-header-right">
              <div className="invoice-number">
                <label>Invoice #</label>
                <span className="invoice-number-display">{created.invoice_number}</span>
              </div>
              <button className="invoice-modal-close" onClick={handleDone}>×</button>
            </div>
          </div>
          <div className="invoice-modal-content">
            {error && <div className="invoice-error">{error}</div>}
            <div className="invoice-success">
              {created.edited ? (
                <>
                  ✅ Invoice {created.invoice_number} was updated in QuickBooks. New total: ${(parseFloat(created.invoice_total) || 0).toFixed(2)}
                  {parseFloat(created.tax) > 0 && ` (includes $${parseFloat(created.tax).toFixed(2)} tax)`}.
                </>
              ) : (
                <>
                  ✅ Invoice {created.invoice_number} was created in QuickBooks
                  {!isJobInvoice && record.price != null && ` for $${(parseFloat(record.price) || 0).toFixed(2)}`}.
                </>
              )}
              {record.invoice_sent_to && created.edited && !created.sent_here ? (
                <><br /><small>📧 The earlier version was emailed to {record.invoice_sent_to}. Send it again so they have this one.</small></>
              ) : record.invoice_sent_to ? (
                <><br /><small>📧 Emailed to {record.invoice_sent_to}.</small></>
              ) : (
                <><br /><small>It has not been emailed to the customer yet.</small></>
              )}
              {!isJobInvoice && (created.edited ? (
                <><br /><small>💡 The work order price now matches the invoice total.</small></>
              ) : (
                <><br /><small>💡 The work order price follows the invoice total. It locks once the invoice is paid in QuickBooks.</small></>
              ))}
            </div>
            <div className="invoice-created-actions">
              <button type="button" className="invoice-send-button" onClick={() => setShowSendDialog(true)}>
                {record.invoice_sent_to ? 'Send Again' : 'Send Invoice'}
              </button>
              <button type="button" className="invoice-print-button" onClick={handlePrintCreated} disabled={printing}>
                {printing ? 'Loading Invoice...' : 'Print Invoice'}
              </button>
              <button type="button" className="invoice-confirm-button" onClick={handleDone}>Done</button>
            </div>
          </div>
        </div>
        {showSendDialog && (
          <SendInvoiceDialog
            isJob={isJobInvoice}
            id={entityId}
            invoiceNumber={created.invoice_number}
            onClose={() => setShowSendDialog(false)}
            onSent={handleSent}
          />
        )}
      </div>
    );
  }

  return (
    <div className="invoice-modal-overlay">
      <div className="invoice-modal">
        <div className="invoice-modal-header">
          <div className="invoice-header-left">
            <h2>{editInvoice ? 'Edit Invoice' : 'Create Invoice'}</h2>
            {qbCompanyInfo && (
              <div className="qb-company-info">
                <small>{qbCompanyInfo.company_name}</small>
              </div>
            )}
          </div>
          <div className="invoice-header-right">
            <div className="invoice-number">
              <label>Invoice #</label>
              <span className="invoice-number-display">{editInvoice ? (existingInvoice?.invoice_number || workOrder.invoice_number) : 'Auto-generated'}</span>
            </div>
            <button className="invoice-modal-close" onClick={handleCancel}>×</button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="invoice-modal-form">
          <div className="invoice-modal-content">
            {error && (
              <div className="invoice-error">
                {error}
              </div>
            )}
            
            {qbNotice && (
              <div className="invoice-error">
                {qbNotice}
              </div>
            )}

            {loadingQbData && (
              <div className="qb-loading">
                <small>Loading QuickBooks data...</small>
              </div>
            )}

            {editInvoice && existingInvoice && (
              <div className="qb-loading">
                <small>
                  Bill to: {existingInvoice.customer_name || 'QuickBooks customer'} · Current total ${(parseFloat(existingInvoice.total) || 0).toFixed(2)}
                  {parseFloat(existingInvoice.balance) < parseFloat(existingInvoice.total) && ` · $${(parseFloat(existingInvoice.balance) || 0).toFixed(2)} still owed`}
                  {' '}· Change the customer or P.O. in QuickBooks.
                </small>
              </div>
            )}

            {/* Customer Section */}
            {!editInvoice && (
            <div className="invoice-section">
              <div className="invoice-section-header">
                <h3>Bill To</h3>
              </div>
              <div className="invoice-section-content">
                {/* QuickBooks Customer Search */}
                {qbCustomers.length > 0 && (
                  <div className="invoice-field-group">
                    <label className="invoice-label">
                      QuickBooks Customer (Search):
                      <div className="customer-search-container">
                        <input
                          type="text"
                          value={customerSearchTerm}
                          onChange={handleCustomerSearch}
                          onFocus={handleCustomerSearchFocus}
                          onBlur={handleCustomerSearchBlur}
                          className="invoice-input"
                          placeholder="Search for a customer..."
                        />
                        {showCustomerDropdown && filteredCustomers.length > 0 && (
                          <div className="customer-dropdown">
                            {filteredCustomers.map(customer => (
                              <div
                                key={customer.id}
                                className="customer-dropdown-item"
                                onClick={() => handleCustomerSelect(customer)}
                              >
                                <div className="customer-name">{customer.name}</div>
                                {customer.company_name && (
                                  <div className="customer-company">{customer.company_name}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                    {invoiceData.selected_customer_id && (
                      <div className="selected-customer-info">
                        <small>Selected: {customerSearchTerm}</small>
                      </div>
                    )}
                    {customerNotice && (
                      <small className="field-help-text">{customerNotice}</small>
                    )}
                  </div>
                )}
                
                <div className="invoice-field-group">
                  <label className="invoice-label">
                    Billing Address Name:
                    <input
                      type="text"
                      name="customer_name"
                      value={invoiceData.customer_name}
                      onChange={handleChange}
                      className="invoice-input"
                      required={!invoiceData.selected_customer_id}
                      placeholder={invoiceData.selected_customer_id ? "Leave blank to use QuickBooks customer address" : "This will be used as the billing address in QuickBooks"}
                    />
                  </label>
                  <small className="field-help-text">
                    {isRetailCustomer(selectedQbCustomer)
                      ? "Filled in with the customer's name because the Retail customer is selected."
                      : invoiceData.selected_customer_id
                        ? "Leave blank to use the customer's address from QuickBooks. Only fill this in if you need a different billing address."
                        : "Required when no QuickBooks customer is selected."
                    }
                  </small>
                </div>

                <div className="invoice-field-group">
                  <label className="invoice-label">
                    P.O. Number:
                    <input
                      type="text"
                      name="po_number"
                      value={invoiceData.po_number}
                      onChange={handleChange}
                      className="invoice-input"
                      placeholder="Enter P.O. Number (optional)"
                    />
                  </label>
                  <small className="field-help-text">
                    
                  </small>
                </div>

                <div className="invoice-field-row">
                  <div className="invoice-field-group">
                    <label className="invoice-label">
                      Invoice Date:
                      <input
                        type="date"
                        name="invoice_date"
                        value={invoiceData.invoice_date}
                        onChange={handleChange}
                        className="invoice-input"
                      />
                    </label>
                  </div>
                  <div className="invoice-field-group">
                    <label className="invoice-label">
                      Due Date:
                      <span className="due-date-display">Auto-set by QuickBooks</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Line Items Section */}
            <div className="invoice-section">
              <div className="invoice-section-header">
                <h3>Items & Services</h3>
                <button type="button" onClick={addLineItem} className="add-line-item-btn">
                  + Add Line
                </button>
              </div>
              <div className="invoice-section-content">
                <div className="line-items-table">
                  <div className="line-items-header">
                    <div className="line-item-col-service-date">Service Date</div>
                    <div className="line-item-col-description">Product/Service</div>
                    <div className="line-item-col-quantity">Qty</div>
                    <div className="line-item-col-rate">Rate ($)</div>
                    <div className="line-item-col-amount">Total ($)</div>
                    <div className="line-item-col-actions">Actions</div>
                  </div>
                  
                  {invoiceData.line_items.map((lineItem, index) => (
                    <div key={lineItem.id} className="line-item-row">
                      <div className="line-item-col-service-date">
                        <input
                          type="date"
                          value={lineItem.service_date || ''}
                          onChange={(e) => handleLineItemChange(lineItem.id, 'service_date', e.target.value)}
                          className="line-item-input"
                        />
                      </div>
                      <div className="line-item-col-description">
                        {/* QuickBooks Items Selection */}
                        {qbItems.length > 0 && (
                          <div>
                            <select
                              value={lineItem.selected_item_id}
                              onChange={(e) => handleItemChange(lineItem.id, e)}
                              className="invoice-select-small"
                            >
                              <option value="">Select item...</option>
                              {qbItems.map(item => (
                                <option key={item.id} value={item.id}>
                                  {item.name} - ${item.unit_price || 0}
                                </option>
                              ))}
                            </select>
                            {lineItem.selected_item_id && (
                              <div style={{fontSize: '11px', color: '#666', marginTop: '2px'}}>
                                Selected: {qbItems.find(item => item.id === lineItem.selected_item_id)?.name || 'Unknown'}
                              </div>
                            )}
                          </div>
                        )}
                        <textarea
                          value={lineItem.description}
                          onChange={(e) => handleLineItemChange(lineItem.id, 'description', e.target.value)}
                          className="line-item-description"
                          rows="2"
                          placeholder="Description"
                        />
                      </div>
                      <div className="line-item-col-quantity">
                        <input
                          type="number"
                          value={lineItem.quantity}
                          onChange={(e) => handleLineItemChange(lineItem.id, 'quantity', e.target.value)}
                          className="line-item-input"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div className="line-item-col-rate">
                        <input
                          type="number"
                          value={lineItem.rate}
                          onChange={(e) => handleLineItemChange(lineItem.id, 'rate', e.target.value)}
                          className="line-item-input"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div className="line-item-col-amount">
                        <input
                          type="number"
                          value={lineItem.amount}
                          onChange={(e) => handleLineItemChange(lineItem.id, 'amount', e.target.value)}
                          className="line-item-input"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div className="line-item-col-actions">
                        {invoiceData.line_items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(lineItem.id)}
                            className="remove-line-item-btn"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Totals Section */}
            <div className="invoice-section">
              <div className="invoice-section-content">
                <div className="invoice-totals">
                  <div className="invoice-totals-left">
                    <div className="invoice-field-group">
                      <label className="invoice-label">
                        Private Note:
                        <textarea
                          name="private_note"
                          value={invoiceData.private_note}
                          onChange={handleChange}
                          className="invoice-textarea"
                          rows="3"
                          placeholder="Add a note to your customer..."
                        />
                      </label>
                    </div>
                  </div>
                  <div className="invoice-totals-right">
                    <div className="totals-table">
                      <div className="total-row">
                        <span className="total-label">Subtotal:</span>
                        <span className="total-value">${(parseFloat(invoiceData.subtotal) || 0).toFixed(2)}</span>
                      </div>
                      {editInvoice ? (
                        <div className="total-row">
                          <span className="total-label">Tax:</span>
                          <span className="total-value">Added by QuickBooks</span>
                        </div>
                      ) : (
                      <div className="total-row">
                        <span className="total-label">
                          Tax Rate (%):
                          <input
                            type="number"
                            name="tax_rate"
                            value={invoiceData.tax_rate}
                            onChange={handleChange}
                            className="tax-rate-input"
                            step="0.01"
                            min="0"
                            max="100"
                          />
                        </span>
                        <span className="total-value">${(parseFloat(invoiceData.tax_amount) || 0).toFixed(2)}</span>
                      </div>
                      )}
                      <div className="total-row total-row-final">
                        <span className="total-label">Total:</span>
                        <span className="total-value">${(parseFloat(invoiceData.total) || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="invoice-modal-footer">
            {!isJobInvoice && !editInvoice && (
              <button
                type="button"
                className="invoice-mark-paid-button"
                onClick={handleMarkAsPaid}
                disabled={loading || markingAsPaid}
              >
                {markingAsPaid ? 'Marking as Paid...' : `Mark as Paid (${paidInvoiceNumber})`}
              </button>
            )}
            <div className="invoice-modal-footer-right">
              <button
                type="button"
                className="invoice-cancel-button"
                onClick={handleCancel}
                disabled={loading || markingAsPaid}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="invoice-confirm-button"
                disabled={loading || markingAsPaid || (editInvoice && !existingInvoice)}
              >
                {editInvoice
                  ? (loading ? 'Saving...' : 'Save Invoice Changes')
                  : (loading ? 'Creating...' : 'Create Invoice')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceModal;
